import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Download } from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-slate-200 text-slate-600",
};

export default function AdminRegistrations() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    const params = filter ? { status_filter: filter } : {};
    api.get("/registrations", { params }).then(({ data }) => setItems(data.items || []));
  }, [filter]);

  return (
    <AdminLayout>
      <div className="p-8 max-w-7xl">
        <div className="label-overline text-[#E63946]">Registrations</div>
        <h1 data-testid="admin-registrations-title" className="font-display text-4xl uppercase tracking-tighter mt-3">
          Camp Registrations
        </h1>

        <div className="mt-6 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            {["", "pending", "confirmed", "cancelled"].map((s) => (
              <button
                key={s || "all"}
                onClick={() => setFilter(s)}
                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm ${
                  filter === s ? "bg-[#0A192F] text-white" : "bg-white border border-slate-300"
                }`}
              >
                {s || "All"}
              </button>
            ))}
          </div>
          <button
            data-testid="export-registrations-csv"
            onClick={() => {
              const params = filter ? { status_filter: filter } : {};
              downloadFile("/admin/registrations/export.csv", params, "cbr-registrations.csv")
                .then(() => toast.success("Registrations CSV exported"))
                .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "Export failed"));
            }}
            className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-slate-50"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left p-4 label-overline">Camp</th>
                <th className="text-left p-4 label-overline">Player</th>
                <th className="text-left p-4 label-overline">Status</th>
                <th className="text-left p-4 label-overline">Attendance</th>
                <th className="text-left p-4 label-overline">Amount</th>
                <th className="text-left p-4 label-overline">Registered</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 last:border-0">
                  <td className="p-4">
                    <div className="font-semibold">{r.camp?.title || "—"}</div>
                    <div className="text-xs text-slate-500">{r.camp?.city}, {r.camp?.province}</div>
                  </td>
                  <td className="p-4">{r.player ? `${r.player.first_name} ${r.player.last_name}` : "—"}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLE[r.status] || "bg-slate-100"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="p-4 text-xs uppercase tracking-wider">{r.attendance_status}</td>
                  <td className="p-4 font-data">${r.amount_due} {r.currency?.toUpperCase()}</td>
                  <td className="p-4 text-xs text-slate-500">{new Date(r.created_at).toLocaleString("en-CA")}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="6" className="p-10 text-center text-sm text-slate-500">No registrations match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
